import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { DonateButton } from "@/components/DonateButton";
import { CLUB_NAV } from "@/data/content";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "About & Timeline", to: "/about" },
  { label: "EduPower13", to: "/edupower13" },
  { label: "Wellbeing", to: "/wellbeing" },
  { label: "Meet the Team", to: "/team" },
  { label: "Events", to: "/events" },
];

const linkClass = ({ isActive }) =>
  `relative font-heading font-semibold text-sm transition-colors duration-300 ${
    isActive ? "text-ember" : "text-coal hover:text-teal"
  }`;

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clubsOpen, setClubsOpen] = useState(false);
  const location = useLocation();
  const lekkaActive = location.pathname.startsWith("/english-is-lekka");

  return (
    <header
      className="sticky top-0 z-50 border-b border-coal/5 bg-sand/85 backdrop-blur-xl"
      data-testid="main-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-2.5">
        <Link to="/" aria-label="EduPower Foundation home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-4" aria-label="Primary">
          <NavLink to="/" end className={linkClass} data-testid="nav-home">
            Home
          </NavLink>
          <NavLink to="/about" className={linkClass} data-testid="nav-about">
            About
          </NavLink>

          <div className="relative group">
            <button
              data-testid="nav-lekka-dropdown"
              className={`flex items-center gap-1 font-heading font-semibold text-sm transition-colors duration-300 ${
                lekkaActive ? "text-ember" : "text-coal group-hover:text-teal"
              }`}
            >
              English is Lekka
              <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-3 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
              <div className="w-60 overflow-hidden rounded-2xl border border-coal/5 bg-white shadow-[0_24px_60px_-20px_rgba(26,29,36,0.35)]">
                {CLUB_NAV.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    end={c.to === "/english-is-lekka"}
                    data-testid={`nav-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `block px-5 py-3 text-sm font-heading font-semibold transition-colors ${
                        isActive ? "bg-teal-subtle text-teal" : "text-coal hover:bg-sand hover:text-ember"
                      }`
                    }
                  >
                    {c.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/edupower13" className={linkClass} data-testid="nav-edupower13">
            EduPower13
          </NavLink>
          <NavLink to="/wellbeing" className={linkClass} data-testid="nav-wellbeing">
            Wellbeing
          </NavLink>
          <NavLink to="/team" className={linkClass} data-testid="nav-team">
            Team
          </NavLink>
          <NavLink to="/events" className={linkClass} data-testid="nav-events">
            Events
          </NavLink>
        </nav>

        <div className="hidden lg:block">
          <DonateButton testId="nav-donate-button" />
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-coal"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-coal/5 bg-sand lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-6 py-4">
              {LINKS.slice(0, 2).map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-xl px-4 py-3 font-heading font-semibold text-coal hover:bg-teal-subtle"
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                data-testid="mobile-lekka-toggle"
                onClick={() => setClubsOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-xl px-4 py-3 font-heading font-semibold text-coal hover:bg-teal-subtle"
              >
                English is Lekka
                <ChevronDown size={16} className={clubsOpen ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {clubsOpen &&
                CLUB_NAV.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    end={c.to === "/english-is-lekka"}
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-xl px-8 py-2.5 text-sm font-heading font-semibold text-coal-muted hover:text-ember"
                  >
                    {c.label}
                  </NavLink>
                ))}
              {LINKS.slice(2).map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-xl px-4 py-3 font-heading font-semibold text-coal hover:bg-teal-subtle"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="pt-2">
                <DonateButton testId="mobile-donate-button" />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
